import {
  Button,
  Checkbox,
  Configuration,
  Flex,
  Form,
  Grid,
  Input,
  ModalManagerContext,
  Text,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import * as deepDiff from 'deep-diff';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { saveChatPayload } from '../../../../interfaces/chat';
import { appState, confirmationAlertProps } from '../../../../interfaces/common';
import {
  fetchAllConfigurationRecords,
  setChatInfo,
  updateBannerInfo,
  updateLoaderDetails,
} from '../../../../redux/actions';
import { usePlatformVersionSelector, useSupportedLanguagesSelector } from '../../../../redux/selectors';
import { ConfirmationAlert } from '../../../Utilities/CustomModal';
import MultiLocaleTitleAndSpecialNote from '../../../Utilities/MultiLocaleTitleAndSpecialNote';
import { addNewChatAccount, deleteAccount, updateChatAccount } from '../ChatApis';
import DisplayToggles from './DisplayToggles';
import SoundNotifications from './SoundNotifications';
import { colorPickerStyle } from './StylingTab';

export const contentType = { Chat: 'Chat_Heading', SpecialNote: 'Special_Note' };

const GeneralTab: React.FC = React.memo(() => {
  const {
    colorTheme,
    accountName,
    welcomeMessage,
    connectionId,
    widgetTitles,
    showSpecialNote = false,
    specialNote = {},
    syncConnectionId,
  } = useSelector((state: appState) => state.chatInfo);
  const { platform: sdkPlatform } = useParams();
  const { sessionId } = useSelector((state: appState) => {
    return {
      accounts: state.accounts,
      sessionId: state.sessionId,
    };
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const languagesSupportedByConsumer = useSupportedLanguagesSelector();
  const firstLanguage = languagesSupportedByConsumer?.[0];
  const [chatHeadings, setChatHeadings] = firstLanguage
    ? useState<any>({ [firstLanguage]: '' })
    : useState<any>({ en: '' });
  const [specialNotes, setSpecialNotes] = firstLanguage
    ? useState<any>({ [firstLanguage]: '' })
    : useState<any>({ en: '' });
  const [enableSpecialNote, setEnableSpecialNote] = useState(showSpecialNote);
  const [specialNoteError, setSpecialNoteError] = useState({});
  const translate = useLocalTranslation();
  const { fullVersion, majorVersion, minorVersion } = usePlatformVersionSelector();
  const beforeEightDotSeven =
    !fullVersion || (majorVersion && (majorVersion < 8 || (majorVersion === 8 && minorVersion && minorVersion < 7)));
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const { register, handleSubmit, errors, reset, setValue, watch } = useForm({ mode: 'all' });
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const { encryptionEnableFlag } = useSelector((state: appState) => state);
  useEffect(() => {
    const formValues = {
      colorTheme: colorTheme || '#0000ff',
      accountName,
      welcomeMessage,
    };
    reset(formValues);
  }, [reset, colorTheme, accountName, welcomeMessage]);

  const widgetTitlesLength = Object.keys(widgetTitles).length;
  const specialNoteLength = Object.keys(specialNote).length;

  useEffect(() => {
    if (widgetTitles && widgetTitlesLength > 0) setChatHeadings({ ...widgetTitles });
    if (specialNote && specialNoteLength > 0) setSpecialNotes({ ...specialNote });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetTitles, specialNote]);

  useEffect(() => {
    setEnableSpecialNote(showSpecialNote);
  }, [showSpecialNote]);

  const deleteChatChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`${translate('chat')} ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [connectionId, dispatch, navigate, sessionId, translate, syncConnectionId]);

  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      { pageName: accountName, deleteChannel: deleteChatChannel },
      { alert: true },
    );
  };

  const actions = (
    <Flex
      container={{
        alignItems: 'start',
      }}
    >
      <Button
        name="Save"
        type="submit"
        variant="primary"
        disabled={
          enableSpecialNote &&
          Object.values(specialNoteError).some((obj: any) => {
            return obj.hasError;
          })
        }
      >
        {translate('save')}
      </Button>
    </Flex>
  );

  const createChatChannel = async (formData: saveChatPayload) => {
    let json,
      operation = '';
    const data: saveChatPayload = { ...formData };
    let newWidgetTitles: any = undefined;

    Object.keys(chatHeadings).forEach((lang) => {
      const trimmedHeading = chatHeadings[lang].trim();
      newWidgetTitles = { ...newWidgetTitles, [lang]: trimmedHeading };
    });
    data['widgetTitles'] = newWidgetTitles;
    data['encryptionEnabled'] = encryptionEnableFlag;
    if (beyondEightDotSeven) {
      data['typingBeforeWelcomeMessage'] = false;
    }
    if (encryptionEnableFlag) {
      data['attachmentIcon'] = false;
      data['displayDownloadTranscript'] = false;
    }
    if (sdkPlatform === 'combined_messaging') {
      data['combinedMessaging'] = 'combined_messaging';
    }
    if (enableSpecialNote) {
      data['showSpecialNote'] = enableSpecialNote;
      data['specialNote'] = specialNotes;
    } else {
      data['showSpecialNote'] = enableSpecialNote;
    }

    const prevValues = {
      accountName,
      welcomeMessage,
      widgetTitles,
      showSpecialNote,
      specialNote,
    };
    const triggerAPI = deepDiff
      .diff(prevValues, data)
      ?.filter((item) => (item as any)?.lhs || (item as any)?.rhs).length;
    const headingCountChange = widgetTitlesLength !== Object.keys(newWidgetTitles).length;
    if (triggerAPI || headingCountChange) {
      dispatch(updateLoaderDetails(true));
      if (connectionId) {
        json = await updateChatAccount(sessionId, connectionId, { ...data, colorTheme });
        operation = 'update';
      } else {
        json = await addNewChatAccount(sessionId, data);
        operation = 'create';
        dispatch(fetchAllConfigurationRecords());
      }
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        dispatch(updateBannerInfo(`Chat account successfully ${operation}d`, 'success'));
        dispatch(setChatInfo(json?.data));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        dispatch(updateBannerInfo(`Failed to ${operation} Chat account`, 'urgent'));
      }
    }
  };

  const addChatHeading = (lang: string, title: string) => {
    const allWidgetTitles = { ...chatHeadings };
    allWidgetTitles[lang] = title;
    setChatHeadings({ ...allWidgetTitles });
  };

  const addSpecialNote = (lang: string, note: string) => {
    const allSpecialNotes = { ...specialNotes, [lang]: note };
    setSpecialNotes({ ...allSpecialNotes });
    if (note.length < 1 || note.length > 350) {
      setSpecialNoteError({ ...specialNoteError, [lang]: { hasError: true } });
    } else {
      setSpecialNoteError({ ...specialNoteError, [lang]: { hasError: false } });
    }
  };

  const updateSpecialNoteToggle = (e: any) => {
    setEnableSpecialNote(e.target.checked);
  };
  const supportedLanguagesUpdated = firstLanguage ? languagesSupportedByConsumer : ['en'];

  const filteredChatHeadings = useMemo(() => {
    return supportedLanguagesUpdated.filter((key: any) => Object.keys(chatHeadings).includes(key));
  }, [chatHeadings, supportedLanguagesUpdated]);

  const filteredSpecialNotes = useMemo(() => {
    return supportedLanguagesUpdated.filter((key: any) => Object.keys(specialNotes).includes(key));
  }, [specialNotes, supportedLanguagesUpdated]);

  const supportedLanguagesLength = supportedLanguagesUpdated.length;
  const showAddHeading = supportedLanguagesLength > filteredChatHeadings.length;
  const showAddSpecialNote = supportedLanguagesLength > filteredSpecialNotes.length;
  const defaultLanguage = firstLanguage ? firstLanguage : 'en';

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 3.5,
      }}
    >
      <Form actions={actions} onSubmit={handleSubmit(createChatChannel)}>
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            <Input
              label={translate('name')}
              type="text"
              name="accountName"
              required={true}
              autoComplete="off"
              info={errors.accountName?.message}
              status={errors.accountName?.message ? 'error' : undefined}
              ref={register({ required: translate('name_empty') })}
            />
          </Grid>

          <Flex container={{ direction: 'column', itemGap: 2 }}>
            {!connectionId && (
              <Text variant="h3" as="h3">
                {translate('appearance')}
              </Text>
            )}
            <Grid
              container={{
                cols: 'repeat(2, 1fr)',
                gap: 2,
                alignItems: errors.colorpicker?.message ? 'center' : 'end',
              }}
            >
              {beforeEightDotSeven && (
                <Input
                  label={translate('chat_heading')}
                  type="text"
                  autoComplete="off"
                  value={chatHeadings?.en}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => addChatHeading('en', e.target.value)}
                />
              )}
              {!connectionId && (
                <Flex
                  container={{
                    direction: 'row',
                    itemGap: 1,
                    alignItems: 'end',
                  }}
                >
                  <Flex item={{ grow: 1 }}>
                    <Input
                      label={translate('primary_color')}
                      type="text"
                      required={true}
                      readOnly={true}
                      name="colorTheme"
                      info={errors.colorTheme?.message}
                      status={errors.colorTheme?.message ? 'error' : undefined}
                      ref={register({ required: translate('color_empty') })}
                    />
                  </Flex>
                  <Input
                    autoComplete="off"
                    type="color"
                    style={colorPickerStyle}
                    value={watch('colorTheme') || colorTheme || '#0000ff'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('colorTheme', e.target.value)}
                  />
                </Flex>
              )}
            </Grid>
            {!beforeEightDotSeven && (
              <>
                <MultiLocaleTitleAndSpecialNote
                  isForChatHeadingOrSpecialNote={contentType.Chat}
                  addHeading={addChatHeading}
                  titleSpecialNoteData={chatHeadings}
                  allowedFilteredLanguages={filteredChatHeadings}
                  defaultLanguage={defaultLanguage}
                  setValues={setChatHeadings}
                  allowNewField={showAddHeading}
                  supportedLanguagesUpdated={supportedLanguagesUpdated}
                  showTextArea={false}
                />
                {connectionId && (
                  <Configuration
                    theme={{
                      components: {
                        'radio-check': {
                          label: {
                            'font-weight': 'bold',
                          },
                        },
                      },
                    }}
                  >
                    <Checkbox
                      name="specialNoteSelection"
                      label={translate('special_note_option')}
                      checked={enableSpecialNote}
                      onChange={updateSpecialNoteToggle}
                    />
                  </Configuration>
                )}
                {enableSpecialNote && (
                  <MultiLocaleTitleAndSpecialNote
                    isForChatHeadingOrSpecialNote={contentType.SpecialNote}
                    addHeading={addSpecialNote}
                    titleSpecialNoteData={specialNotes}
                    allowedFilteredLanguages={filteredSpecialNotes}
                    defaultLanguage={defaultLanguage}
                    setValues={setSpecialNotes}
                    allowNewField={showAddSpecialNote}
                    supportedLanguagesUpdated={supportedLanguagesUpdated}
                    showTextArea={true}
                    specialNoteError={specialNoteError}
                    setSpecialNoteError={setSpecialNoteError}
                  />
                )}
                <Text>
                  <b>{translate('note')}:</b> {translate('widget_languages_note')}
                </Text>
              </>
            )}
            {beforeEightDotSeven && (
              <Input
                name="welcomeMessage"
                label={translate('display_message')}
                type="text"
                required={true}
                autoComplete="off"
                info={errors.welcomeMessage?.message}
                status={errors.welcomeMessage?.message ? 'error' : undefined}
                ref={register({
                  required: translate('display_message_empty'),
                })}
              />
            )}
          </Flex>
        </Flex>
      </Form>
      {connectionId && <SoundNotifications />}
      {connectionId && <DisplayToggles />}
      {connectionId && (
        <Flex>
          <Button variant="secondary" onClick={deleteConfirmation}>
            {translate('delete_connection_button')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
});

export default GeneralTab;
